"use client";

import { useRef, useState } from "react";
import { Send, Sparkles, ThumbsDown, ThumbsUp } from "lucide-react";
import { errorMessage, mutation } from "@/lib/api";
import { useResource } from "@/lib/use-resource";
import { Busy, ErrorNotice } from "@/components/ui";
import { readLogPreference } from "@/components/assistant-preferences";
import { toast } from "@/components/toast-provider";

type Action = { type: "power" | "navigate"; value: string; label: string };
type Finding = { id: string; title: string };
type Entry =
  | { role: "user"; content: string }
  | { role: "assistant"; content: string; id?: string; action?: Action | null; findings?: Finding[]; rating?: number; fixed?: boolean; acted?: boolean };

type Status = {
  available: boolean;
  name: string;
  welcome: string;
  placeholder: string;
  starters: string[];
  allowLogs: boolean;
  collectFeedback: boolean;
  maxQuestionChars: number;
  trainingOptOut: boolean;
};

export function ServerAssistant({
  id,
  canReadLogs,
  onPower,
  onNavigate,
  onReply,
}: {
  id: string;
  canReadLogs: boolean;
  onPower: (action: string) => Promise<void>;
  onNavigate: (tab: string) => void;
  onReply?: () => void;
}) {
  const status = useResource<Status>("/assistant");
  const [entries, setEntries] = useState<Entry[]>([]);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState<number | null>(null);
  const bottom = useRef<HTMLDivElement>(null);

  const patch = (index: number, change: Partial<Extract<Entry, { role: "assistant" }>>) =>
    setEntries((old) => old.map((entry, i) => (i === index && entry.role === "assistant" ? { ...entry, ...change } : entry)));

  async function send(text: string) {
    const message = text.trim();
    if (!message || pending) return;
    const history = entries.map(({ role, content }) => ({ role, content }));
    setEntries((old) => [...old, { role: "user", content: message }]);
    setDraft("");
    setPending(true);
    setError("");
    try {
      const out = await mutation<{ reply: string; id?: string; action?: Action | null; findings?: Finding[] }>(
        `/servers/${encodeURIComponent(id)}/assistant`,
        { message, history, useLog: canReadLogs && readLogPreference() },
      );
      setEntries((old) => [...old, { role: "assistant", content: out.reply, id: out.id, action: out.action, findings: out.findings ?? [] }]);
      onReply?.();
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setPending(false);
      setTimeout(() => bottom.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 50);
    }
  }

  function feedback(index: number, entry: Extract<Entry, { role: "assistant" }>, body: { rating?: number; outcome?: string }) {
    if (!entry.id) return;
    void mutation("/assistant/feedback", { id: entry.id, ...body }).catch((cause) => toast({ tone: "error", title: "Feedback not saved", message: errorMessage(cause) }));
    patch(index, { rating: body.rating ?? entry.rating, fixed: body.outcome === "fixed" ? true : entry.fixed });
  }

  async function run(index: number, entry: Extract<Entry, { role: "assistant" }>) {
    const action = entry.action;
    if (!action) return;
    setConfirming(null);
    try {
      if (action.type === "power") await onPower(action.value);
      else onNavigate(action.value);
      patch(index, { acted: true });
      if (entry.id) void mutation("/assistant/feedback", { id: entry.id, outcome: "action_taken" }).catch(() => undefined);
    } catch (cause) {
      setError(errorMessage(cause));
    }
  }

  if (status.loading) return <Busy label="Loading assistant" />;
  if (!status.data?.available) {
    return <ErrorNotice message={status.error || "The assistant is not enabled on this panel."} />;
  }

  return (
    <>
      <div className="toolbar">
        <h2><Sparkles size={18} /> {status.data.name}</h2>
      </div>
      <div className="assistant">
        <div className="assistant-thread">
          {entries.length === 0 && (
            <div className="assistant-empty">
              <p>{status.data.welcome}</p>
              <div className="assistant-starters">
                {status.data.starters.map((text) => (
                  <button key={text} className="button" onClick={() => void send(text)}>{text}</button>
                ))}
              </div>
            </div>
          )}
          {entries.map((entry, index) =>
            entry.role === "user" ? (
              <div key={index} className="assistant-msg assistant-user">{entry.content}</div>
            ) : (
              <div key={index} className="assistant-msg assistant-bot">
                {entry.findings && entry.findings.length > 0 && (
                  <p className="assistant-finding">Crash doctor: {entry.findings.map((finding) => finding.title).join(" · ")}</p>
                )}
                <div className="assistant-text">{entry.content}</div>
                {entry.action && !entry.acted && (
                  confirming === index ? (
                    <div className="assistant-confirm">
                      <span>{entry.action.type === "power" ? `${entry.action.label}?` : `${entry.action.label}?`}</span>
                      <button className="button button-primary" onClick={() => void run(index, entry)}>Confirm</button>
                      <button className="button" onClick={() => setConfirming(null)}>Cancel</button>
                    </div>
                  ) : (
                    <button className="button button-primary" onClick={() => setConfirming(index)}>{entry.action.label}</button>
                  )
                )}
                {entry.acted && <p className="muted">Done.</p>}
                {entry.id && (
                  <div className="assistant-feedback">
                    <button className={`icon-button ${entry.rating === 1 ? "active" : ""}`} title="Helpful" onClick={() => feedback(index, entry, { rating: 1 })}><ThumbsUp size={14} /></button>
                    <button className={`icon-button ${entry.rating === -1 ? "active" : ""}`} title="Not helpful" onClick={() => feedback(index, entry, { rating: -1 })}><ThumbsDown size={14} /></button>
                    <button className="button" disabled={entry.fixed} onClick={() => feedback(index, entry, { rating: 1, outcome: "fixed" })}>{entry.fixed ? "Marked fixed" : "This fixed it"}</button>
                  </div>
                )}
              </div>
            ),
          )}
          {pending && <div className="assistant-msg assistant-bot muted">Thinking… this can take up to 30 seconds.</div>}
          <div ref={bottom} />
        </div>
        <ErrorNotice message={error} />
        <form
          className="assistant-form"
          onSubmit={(event) => {
            event.preventDefault();
            void send(draft);
          }}
        >
          <textarea
            value={draft}
            rows={2}
            maxLength={status.data.maxQuestionChars}
            placeholder={status.data.placeholder}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                void send(draft);
              }
            }}
          />
          <button className="button button-primary" disabled={pending || !draft.trim()}><Send size={15} />Send</button>
        </form>
      </div>
    </>
  );
}
